import {Rectangle} from 'recharts';
import {useTheme} from 'styled-components';

import {isNotNil} from 'ramda';

import {PriceReportType} from '../../../../../types/PriceReportType';
import {StatisticsChartItem} from '../types/StatisticsChartItem';
import {getChartItemColor} from '../utils/getChartItemColor';
import {MyVehicleShape} from './MyVehicleShape';

interface BarShapeProps {
  x?: number; // Passed by recharts
  y?: number; // Passed by recharts
  width?: number; // Passed by recharts
  dataKey?: string; // Passed by recharts
  payload?: StatisticsChartItem; // Passed by recharts
  priceReport: PriceReportType;
  activeTooltipIndex: number | null;
  selectedVehicleIds: string[] | null;
}

export function BarShape(props: BarShapeProps) {
  const theme = useTheme();

  const isMyVehicle = props.payload?.car.isMyVehicle;
  const isMyAd = props.payload?.car.isMyAd;
  const isFocused = isNotNil(props.payload) && props.activeTooltipIndex === props.payload.index;
  const isSelected =
    isNotNil(props.payload) && !!props.selectedVehicleIds?.includes(props.payload.car.ad_id);

  const isLowerBar = props.dataKey === 'y1';
  const isHigherBar = props.dataKey === 'y2';
  const lowerBarHeight = props.payload?.y1 ?? 0;
  const higherBarHeight = props.payload?.y2 ?? 0;
  const totalBarHeight = props.payload?.y ?? 0;
  const isPositiveBar = totalBarHeight > lowerBarHeight;
  const isLowerBarShown = true;
  const isHigherBarShown = totalBarHeight > lowerBarHeight || isMyAd;
  const isThisBarShown = (isLowerBar && isLowerBarShown) || (isHigherBar && isHigherBarShown);
  const isLowerBarLight = false;
  const isHigherBarLight = isMyAd && !(isPositiveBar && (isFocused || isSelected));
  const isThisBarLight = (isHigherBar && isHigherBarLight) || (isLowerBar && isLowerBarLight);
  const hasLowerBarRadius = !isHigherBarShown || higherBarHeight === 0;
  const hasHigherBarRadius = !hasLowerBarRadius;
  const hasThisBarRadius = (isLowerBar && hasLowerBarRadius) || (isHigherBar && hasHigherBarRadius);

  if (!isThisBarShown) {
    return null;
  }

  if (isMyVehicle) {
    return <MyVehicleShape {...props} chartType="bar" />;
  }

  const color = props.payload
    ? getChartItemColor(
        theme,
        props.priceReport,
        props.payload?.car,
        isFocused,
        isSelected,
        isThisBarLight
      )
    : undefined;

  return (
    <Rectangle {...props} fill={color} radius={hasThisBarRadius ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
  );
}
